import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { CheckCircleIcon, SparklesIcon } from "@heroicons/react/24/solid";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import InputChipComponent from "../../components/InputChipComponent/InputChipComponent";
import { environment } from "../../environments/environment";
import { useAuthContext } from "../../hooks/useAuthContext";
import { ONBOARDING_DONE_KEY } from "./OnboardingWelcome";

// Use cases that match what LeadZLab does: find leads where people are looking for freelancers/help.
const USE_CASE_OPTIONS = [
  { id: "freelance-gigs", label: "Find clients or gigs", sublabel: "I'm a freelancer and want to find people posting “looking for…” or “need a…”" },
  { id: "recommendations", label: "Get recommended", sublabel: "I want to be suggested when people ask “who can do X?” or “anyone know a good…”" },
  { id: "market-research", label: "See what people are asking", sublabel: "I want to listen to what's being discussed or requested" },
  { id: "other", label: "Something else", sublabel: "Different goal" },
];

// Shown after they pick "Find clients or gigs" or "Get recommended" – who is this for?
const PRIMARY_SERVICE_OPTIONS = [
  { id: "design", label: "Design" },
  { id: "development", label: "Development" },
  { id: "writing", label: "Writing" },
  { id: "marketing", label: "Marketing" },
  { id: "other", label: "Other" },
];

const CHANNELS = [
  { id: "reddit", label: "Reddit", description: "No extension needed. We'll run your scout on a schedule." },
  {
    id: "linkedin",
    label: "LinkedIn",
    description: "Requires the LeadZLab browser extension. After we create your scout, install the extension and connect LinkedIn to start.",
  },
];

const TOTAL_STEPS = 6; // Intent, Profile, Channel, Scout, Schedule, Success

function buildLinkedInQuery(keywords) {
  if (!keywords.length) return "";
  return keywords.map((k) => k.trim()).join(" or ");
}

export default function OnboardingWizard() {
  const { user, dispatch } = useAuthContext();
  const navigate = useNavigate();

  const markOnboardingComplete = async () => {
    try {
      await axios.put(
        `${environment.BACKEND}/user/onboarding/complete`,
        {},
        { headers: { Authorization: `Bearer ${user?.accessToken}` } }
      );
      const updated = { ...user, onboardingDone: true };
      Cookies.set("user", JSON.stringify(updated), { expires: 365 });
      dispatch({ type: "UPDATE_USER", payload: { onboardingDone: true } });
    } catch (_) {
      // still set localStorage so redirect works if backend fails
    }
    try {
      localStorage.setItem(ONBOARDING_DONE_KEY, "true");
    } catch {
      // ignore
    }
  };
  const [step, setStep] = useState(1);
  const [useCase, setUseCase] = useState("");
  const [primaryService, setPrimaryService] = useState("");
  const [primaryServiceOther, setPrimaryServiceOther] = useState(""); // custom "Other" text
  const [channel, setChannel] = useState("");
  const [jobName, setJobName] = useState("My first scout");
  const [keywords, setKeywords] = useState([]);
  const [subreddits, setSubreddits] = useState([]);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);

  // Scout profile (prompt-like)
  const [profileWhatIDo, setProfileWhatIDo] = useState("");
  const [profileLookingFor, setProfileLookingFor] = useState("");
  const [profilePortfolioUrl, setProfilePortfolioUrl] = useState("");
  const [profileExtra, setProfileExtra] = useState("");
  const [outreachMessages, setOutreachMessages] = useState(["", "", ""]);
  const [showOptionalProfile, setShowOptionalProfile] = useState(false);

  const isReddit = channel === "reddit";

  const handleStep1Next = () => {
    if (!useCase) return;
    setStep(2);
  };

  const handleProfileNext = async () => {
    setProfileLoading(true);
    try {
      await axios.put(
        `${environment.BACKEND}/user/onboarding/profile`,
        {
          profileWhatIDo: profileWhatIDo.trim(),
          profileLookingFor: profileLookingFor.trim(),
          profilePortfolioUrl: profilePortfolioUrl.trim(),
          profileExtra: profileExtra.trim(),
          outreachMessages: outreachMessages.map((m) => m.trim()).filter(Boolean),
        },
        { headers: { Authorization: `Bearer ${user?.accessToken}` } }
      );
      setStep(3);
    } catch (err) {
      toast.error(err?.response?.data?.message ?? "Could not save profile");
    } finally {
      setProfileLoading(false);
    }
  };

  const handleProfileSkip = () => {
    setStep(3);
  };

  const needsServiceContext = useCase === "freelance-gigs" || useCase === "recommendations";
  const resolvedPrimaryService =
    needsServiceContext && primaryService === "other" && primaryServiceOther.trim()
      ? primaryServiceOther.trim()
      : primaryService;

  const handleStep2Next = () => {
    if (!channel) return;
    setStep(4);
  };

  const fetchSuggestion = async () => {
    if (!channel || !useCase) return;
    setSuggestionLoading(true);
    try {
      const { data: res } = await axios.post(
        `${environment.BACKEND}/user/onboarding/auto-generate-matcher`,
        { useCase, primaryService: resolvedPrimaryService || undefined, channel },
        { headers: { Authorization: `Bearer ${user?.accessToken}` } }
      );
      const d = res?.data || res;
      if (d?.jobName) setJobName(d.jobName);
      if (!isReddit && Array.isArray(d?.keywords) && d.keywords.length) setKeywords(d.keywords);
      if (Array.isArray(d?.subreddits) && d.subreddits.length) setSubreddits(d.subreddits);
    } catch (err) {
      toast.error(err?.response?.data?.message ?? "Could not generate suggestion");
    } finally {
      setSuggestionLoading(false);
    }
  };

  const fetchScoutFromProfile = async () => {
    if (!channel) return;
    setSuggestionLoading(true);
    try {
      const { data: res } = await axios.post(
        `${environment.BACKEND}/user/onboarding/generate-scout-from-profile`,
        { channel },
        { headers: { Authorization: `Bearer ${user?.accessToken}` } }
      );
      const d = res?.data || res;
      if (d?.jobName) setJobName(d.jobName);
      if (!isReddit && Array.isArray(d?.keywords) && d.keywords.length) setKeywords(d.keywords);
      if (Array.isArray(d?.subreddits) && d.subreddits.length) setSubreddits(d.subreddits);
      toast.success("Scout generated from your profile");
    } catch (err) {
      toast.error(err?.response?.data?.message ?? "Could not generate from profile");
    } finally {
      setSuggestionLoading(false);
    }
  };

  useEffect(() => {
    if (step === 4 && channel && useCase) {
      fetchSuggestion();
    }
  }, [step, channel, useCase]);

  const handleStep3Next = () => {
    if (isReddit) {
      if (subreddits.length === 0) {
        toast.error("Add at least one subreddit.");
        return;
      }
    } else if (keywords.length === 0) {
      toast.error("Add at least one keyword.");
      return;
    }
    setStep(5);
  };

  const handleStep4Submit = async () => {
    if (isReddit) {
      if (subreddits.length === 0) {
        toast.error("Add at least one subreddit.");
        return;
      }
    } else {
      if (keywords.length === 0) {
        toast.error("Add at least one keyword.");
        return;
      }
    }

    const payload = isReddit
      ? { jobName, subreddits, timeFilter: "day", type: channel }
      : {
          jobName,
          q: buildLinkedInQuery(keywords),
          timeFilter: "day",
          type: channel,
        };

    setCreating(true);
    try {
      await axios.post(
        `${environment.BACKEND}/resources/job`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.accessToken}`,
          },
        }
      );
      try {
        localStorage.setItem(ONBOARDING_DONE_KEY, "true");
        localStorage.setItem("leadzlab_first_matcher_created", "true");
      } catch {
        // ignore
      }
      await markOnboardingComplete();
      setStep(6);
      toast.success("Scout created!");
    } catch (err) {
      toast.error(err?.response?.data?.message ?? err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleGoToDashboard = () => {
    navigate("/dashboard");
  };

  const handleSkip = async () => {
    await markOnboardingComplete();
    navigate("/dashboard");
  };

  return (
    <div className="mx-auto max-w-xl px-4 py-6">
      {step < 6 && (
        <div className="mb-5">
          <div className="flex justify-between text-sm text-slate-500">
            <span>Step {step} of {TOTAL_STEPS - 1}</span>
          </div>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
            <div
              className="h-full rounded-full bg-emerald-600 transition-all duration-300"
              style={{ width: `${(step / (TOTAL_STEPS - 1)) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Step 1 – Goal, then context (who is this for?) */}
      {step === 1 && (
        <>
          <h2 className="text-xl font-bold text-slate-900">Why are you here?</h2>
          <p className="mt-1 text-slate-600">We&apos;ll set up a scout tailored to this.</p>
          <ul className="mt-6 space-y-2">
            {USE_CASE_OPTIONS.map((opt) => (
              <li key={opt.id}>
                <button
                  type="button"
                  onClick={() => {
                    setUseCase(opt.id);
                    setPrimaryService("");
                    setPrimaryServiceOther("");
                  }}
                  className={`w-full rounded-xl border-2 px-4 py-3 text-left transition ${
                    useCase === opt.id
                      ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                      : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                  }`}
                >
                  <span className="block font-medium text-slate-900">{opt.label}</span>
                  {opt.sublabel && (
                    <span className="mt-0.5 block text-xs text-slate-500">{opt.sublabel}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>

          {/* Context question shown only after a use case is selected */}
          {useCase && (
            <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
              {needsServiceContext && (
                <>
                  <p className="text-sm font-medium text-slate-800">What do you do?</p>
                  <p className="mt-0.5 text-xs text-slate-500">So we can suggest the right keywords and places.</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {PRIMARY_SERVICE_OPTIONS.map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setPrimaryService(opt.id)}
                        className={`rounded-lg px-3 py-2 text-sm font-medium transition ${
                          primaryService === opt.id
                            ? "bg-emerald-600 text-white"
                            : "bg-white border border-slate-200 text-slate-700 hover:border-slate-300"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {primaryService === "other" && (
                    <input
                      type="text"
                      value={primaryServiceOther}
                      onChange={(e) => setPrimaryServiceOther(e.target.value)}
                      placeholder="e.g. consulting, data, video"
                      className="mt-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    />
                  )}
                </>
              )}
              {useCase === "market-research" && (
                <>
                  <p className="text-sm font-medium text-slate-800">What topic or industry? (optional)</p>
                  <input
                    type="text"
                    value={primaryService}
                    onChange={(e) => setPrimaryService(e.target.value)}
                    placeholder="e.g. SaaS, fitness, fintech"
                    className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </>
              )}
              {useCase === "other" && (
                <>
                  <p className="text-sm font-medium text-slate-800">Tell us a bit more (optional)</p>
                  <input
                    type="text"
                    value={primaryService}
                    onChange={(e) => setPrimaryService(e.target.value)}
                    placeholder="So we can tailor your scout"
                    className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </>
              )}
            </div>
          )}

          <div className="mt-8 flex flex-col items-stretch gap-3 sm:flex-row sm:justify-between sm:items-center">
            <button
              type="button"
              onClick={handleSkip}
              className="order-2 text-sm text-slate-500 hover:text-slate-700 sm:order-1"
            >
              Skip for now
            </button>
            <button
              type="button"
              onClick={handleStep1Next}
              disabled={!useCase}
              className="order-1 rounded-xl bg-emerald-600 px-6 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50 sm:order-2"
            >
              Continue
            </button>
          </div>
        </>
      )}

      {/* Step 2 – Tell us about yourself (scout profile) */}
      {step === 2 && (
        <>
          <button
            type="button"
            onClick={() => setStep(1)}
            className="mb-2 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          >
            <ChevronLeftIcon className="h-4 w-4" /> Back
          </button>
          <h2 className="text-xl font-bold text-slate-900">Tell us about yourself</h2>
          <p className="mt-0.5 text-sm text-slate-600">We&apos;ll use this to suggest better keywords and places. You can skip or add more later from your profile.</p>
          <div className="mt-4 space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">What do you do?</label>
              <textarea
                value={profileWhatIDo}
                onChange={(e) => setProfileWhatIDo(e.target.value)}
                placeholder="e.g. I design brand identities and marketing assets for startups"
                rows={2}
                className="mt-0.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">What are you looking for?</label>
              <textarea
                value={profileLookingFor}
                onChange={(e) => setProfileLookingFor(e.target.value)}
                placeholder='e.g. Clients posting "looking for a designer" or "need a logo"'
                rows={2}
                className="mt-0.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>
            {showOptionalProfile ? (
              <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/50 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Optional</span>
                  <button type="button" onClick={() => setShowOptionalProfile(false)} className="text-xs text-slate-500 hover:text-slate-700">Collapse</button>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600">Portfolio or website</label>
                  <input type="url" value={profilePortfolioUrl} onChange={(e) => setProfilePortfolioUrl(e.target.value)} placeholder="https://..." className="mt-0.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600">Anything else we should know?</label>
                  <textarea value={profileExtra} onChange={(e) => setProfileExtra(e.target.value)} placeholder="e.g. I prefer B2B, or I focus on SaaS" rows={2} className="mt-0.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600">Outreach message examples</label>
                  <p className="mt-0.5 text-xs text-slate-500">Stored for later; we&apos;ll use these in future features.</p>
                  {[0, 1, 2].map((i) => (
                    <input key={i} type="text" value={outreachMessages[i] ?? ""} onChange={(e) => { const next = [...outreachMessages]; while (next.length <= i) next.push(""); next[i] = e.target.value; setOutreachMessages(next.slice(0, 5)); }} placeholder={`Example ${i + 1}`} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500" />
                  ))}
                </div>
              </div>
            ) : (
              <button type="button" onClick={() => setShowOptionalProfile(true)} className="text-sm text-slate-500 hover:text-slate-700">+ Add optional details (portfolio, notes, outreach examples)</button>
            )}
          </div>
          <div className="mt-6 flex flex-col items-stretch gap-3 sm:flex-row sm:justify-between sm:items-center">
            <button
              type="button"
              onClick={handleProfileSkip}
              className="order-2 text-sm text-slate-500 hover:text-slate-700 sm:order-1"
            >
              Skip for now
            </button>
            <button
              type="button"
              onClick={handleProfileNext}
              disabled={profileLoading}
              className="order-1 rounded-xl bg-emerald-600 px-6 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50 sm:order-2"
            >
              {profileLoading ? "Saving…" : "Continue"}
            </button>
          </div>
        </>
      )}

      {/* Step 3 – Channel */}
      {step === 3 && (
        <>
          <button
            type="button"
            onClick={() => setStep(2)}
            className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          >
            <ChevronLeftIcon className="h-4 w-4" /> Back
          </button>
          <h2 className="text-xl font-bold text-slate-900">Where do you want to look first?</h2>
          <p className="mt-1 text-slate-600">You can add more channels later from your dashboard.</p>
          <ul className="mt-6 space-y-3">
            {CHANNELS.map((opt) => (
              <li key={opt.id}>
                <button
                  type="button"
                  onClick={() => setChannel(opt.id)}
                  className={`w-full rounded-xl border-2 px-4 py-3 text-left transition ${
                    channel === opt.id
                      ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                      : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                  }`}
                >
                  <span className="block text-sm font-medium">{opt.label}</span>
                  <span className="mt-1 block text-xs text-slate-500">{opt.description}</span>
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-col items-end gap-3">
            <Link
              to={`/create-job?useCase=${encodeURIComponent(useCase)}&channel=${encodeURIComponent(channel)}`}
              className="text-sm text-slate-500 hover:text-slate-700"
            >
              Prefer to set it up yourself?
            </Link>
            <button
              type="button"
              onClick={handleStep2Next}
              disabled={!channel}
              className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* Step 4 – Scout config (AI suggestion or from profile) */}
      {step === 4 && (
        <>
          <button
            type="button"
            onClick={() => setStep(3)}
            className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          >
            <ChevronLeftIcon className="h-4 w-4" /> Back
          </button>
          <h2 className="text-xl font-bold text-slate-900">
            Here&apos;s your scout for {USE_CASE_OPTIONS.find((o) => o.id === useCase)?.label ?? useCase} on {channel === "reddit" ? "Reddit" : "LinkedIn"}
          </h2>
          <p className="mt-1 text-slate-600">{isReddit ? "You can edit the name and subreddits below." : "You can edit the name and keywords below."}</p>

          {suggestionLoading ? (
            <div className="mt-8 flex flex-col items-center justify-center py-12">
              <SparklesIcon className="h-12 w-12 animate-pulse text-emerald-500" />
              <p className="mt-4 text-sm text-slate-600">Generating your scout…</p>
            </div>
          ) : (
            <div className="mt-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700">Matcher name</label>
                <input
                  type="text"
                  value={jobName}
                  onChange={(e) => setJobName(e.target.value)}
                  placeholder="e.g. Freelance gigs"
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </div>
              {isReddit ? (
                <div>
                  <label className="block text-sm font-medium text-slate-700">Subreddits</label>
                  <InputChipComponent
                    keywords={subreddits}
                    setKeywords={setSubreddits}
                    placeholder="e.g. forhire, startups"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-slate-700">Keywords</label>
                  <InputChipComponent
                    keywords={keywords}
                    setKeywords={setKeywords}
                    placeholder="e.g. looking for, need a developer"
                  />
                </div>
              )}
            </div>
          )}

          {!suggestionLoading && (
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
              <button
                type="button"
                onClick={fetchScoutFromProfile}
                disabled={suggestionLoading}
                className="inline-flex items-center gap-2 rounded-xl border-2 border-emerald-500 bg-emerald-50 px-4 py-2.5 text-sm font-medium text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
              >
                <SparklesIcon className="h-5 w-5" />
                Create from my profile
              </button>
              <button
                type="button"
                onClick={handleStep3Next}
                disabled={isReddit ? subreddits.length === 0 : keywords.length === 0}
                className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Step 5 – Schedule + Create scout */}
      {step === 5 && (
        <>
          <button
            type="button"
            onClick={() => setStep(4)}
            className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          >
            <ChevronLeftIcon className="h-4 w-4" /> Back
          </button>
          <h2 className="text-xl font-bold text-slate-900">Almost there</h2>
          <p className="mt-1 text-slate-600">We check this scout regularly. New posts from the past 24 hours are filtered; repeats are ignored.</p>
          <div className="mt-8 flex justify-end">
            <button
              type="button"
              onClick={handleStep4Submit}
              disabled={creating}
              className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-70"
            >
              {creating ? "Creating…" : "Create scout"}
            </button>
          </div>
        </>
      )}

      {/* Step 6 – Success */}
      {step === 6 && (
        <>
          <div className="flex flex-col items-center text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
              <CheckCircleIcon className="h-10 w-10 text-emerald-600" />
            </div>
            <h2 className="mt-6 text-xl font-bold text-slate-900">Your scout is live</h2>
            {isReddit ? (
              <p className="mt-2 text-slate-600">
                We&apos;ll run it on schedule and email you when we find new leads. You can view results and manage scouts from your dashboard.
              </p>
            ) : (
              <p className="mt-2 text-slate-600">
                Install the LeadZLab browser extension and connect LinkedIn to start finding leads. You can manage this scout from your dashboard.
              </p>
            )}
            <button
              type="button"
              onClick={handleGoToDashboard}
              className="mt-8 w-full rounded-xl bg-emerald-600 px-5 py-3.5 text-base font-semibold text-white hover:bg-emerald-700"
            >
              Go to dashboard
            </button>
          </div>
        </>
      )}
    </div>
  );
}
